// src/api/index.js

// ─── Базовые адреса API ──────────────────────────────────────────
// Два хоста намеренно:
//   • DIRECT — поддомен api4 в режиме DNS-only (серое облако в Cloudflare):
//     резолвится в ЧИСТЫЙ IPv4 сервера, без IPv6. Быстрый, не виснет на
//     мёртвом IPv6 домашних провайдеров. Основной путь.
//   • CLOUDFLARE — api через Cloudflare (оранжевое облако): прячет IP Hetzner,
//     работает оттуда, где прямой IP заблокирован (например, РФ через VPN).
//     Подстраховка на случай, если прямой путь недоступен.
// Приложение само определяет рабочий хост и запоминает его между сессиями.
const DIRECT_BASE     = 'https://api4.alsatuu.biz/api';
const CLOUDFLARE_BASE = 'https://api.alsatuu.biz/api';

// Адрес из окружения (.env → REACT_APP_API_URL).
// ВАЖНО: полностью переопределяет логику ТОЛЬКО для локальной разработки
// (localhost / адрес в домашней сети). Если там указан боевой адрес, он
// становится лишь ПОДСКАЗКОЙ — пробуем его первым, но резервный хост
// остаётся рабочим. Иначе одна строка в .env молча отключала бы весь
// механизм переключения, и приложение висело бы на недоступном хосте.
const RAW_ENV_BASE = process.env.REACT_APP_API_URL || null;
const IS_LOCAL_ENV = !!RAW_ENV_BASE
  && /(localhost|127\.0\.0\.1|10\.\d+\.|192\.168\.|172\.(1[6-9]|2\d|3[01])\.)/.test(RAW_ENV_BASE);
const ENV_BASE = IS_LOCAL_ENV ? RAW_ENV_BASE : null;

const BASES = (() => {
  const list = [DIRECT_BASE, CLOUDFLARE_BASE];
  if (RAW_ENV_BASE && !IS_LOCAL_ENV) {
    const norm = String(RAW_ENV_BASE).replace(/\/$/, '');
    if (!list.includes(norm)) list.unshift(norm);
  }
  return list;
})();

// Таймауты по типам запросов (миллисекунды)
const TIMEOUT_DEFAULT = 15000;        // 15с — обычные запросы
const TIMEOUT_UPLOAD  = 60000;        // 60с — загрузка файлов
const TIMEOUT_AUTH    = 12000;        // 12с — auth (при отказе есть второй хост)
const PROBE_TIMEOUT   = 4000;         // 4с — проверка «жив ли хост»

const LS_BASE_KEY = 'apiBase';

// Запомненный хост прошлой сессии — это лишь ПОДСКАЗКА (какой пробовать
// первым), а не готовый ответ: сеть могла смениться (дом/мобильный/VPN).
let resolvedBase = (() => {
  if (ENV_BASE) return ENV_BASE;
  try {
    const saved = localStorage.getItem(LS_BASE_KEY);
    if (saved && BASES.includes(saved)) return saved;
  } catch (e) {}
  return null;
})();

let resolving = null;  // промис текущего резолва, чтобы не запускать параллельно

// Лёгкая проверка доступности хоста. ЛЮБОЙ HTTP-ответ (даже 401) значит «хост жив».
// /auth/me без токена быстро отдаёт 401 — идеальный пробник связи.
async function probeBase(base) {
  const controller = new AbortController();
  const tid = setTimeout(() => controller.abort(), PROBE_TIMEOUT);
  try {
    await fetch(`${base}/auth/me`, { method: 'GET', signal: controller.signal });
    clearTimeout(tid);
    return true;
  } catch (e) {
    clearTimeout(tid);
    return false;   // не ответил за отведённое время — считаем недоступным
  }
}

function rememberBase(base) {
  resolvedBase = base;
  try { localStorage.setItem(LS_BASE_KEY, base); } catch (e) {}
}

// ГОНКА: оба хоста проверяются ОДНОВРЕМЕННО, побеждает первый ответивший.
// Это ключевой момент — мёртвый путь (например, зависший IPv6 у домашнего
// провайдера) просто проигрывает гонку и никого не задерживает.
function resolveBase() {
  return new Promise((resolve) => {
    let settled = false;
    let left = BASES.length;
    BASES.forEach((base) => {
      probeBase(base).then((ok) => {
        if (settled) return;
        if (ok) {
          settled = true;
          rememberBase(base);
          resolve(base);
          return;
        }
        left -= 1;
        if (left === 0) {
          // Не ответил никто (нет сети). Вернём подсказку — запрос сам
          // выдаст понятную ошибку, кэш при этом сбрасываем.
          try { localStorage.removeItem(LS_BASE_KEY); } catch (e) {}
          resolve(resolvedBase || BASES[0]);
        }
      });
    });
  });
}

// Текущий рабочий хост.
// ВАЖЕН ПОРЯДОК: если прогрев/резолв уже идёт — ждём его, и только потом
// смотрим на кэш. Иначе запрос уходил на непроверенный хост из прошлой
// сессии и висел до таймаута, а прогрев завершался уже впустую.
async function getBase() {
  if (ENV_BASE) return ENV_BASE;
  if (resolving) return resolving;
  if (resolvedBase) return resolvedBase;
  resolving = resolveBase().finally(() => { resolving = null; });
  return resolving;
}

// Прогрев соединения при старте приложения: заранее находит рабочий хост,
// чтобы первый же логин/запрос шёл по готовому пути без задержки.
export function warmUpConnection() {
  if (ENV_BASE) return Promise.resolve(ENV_BASE);
  if (!resolving) {
    resolving = resolveBase().finally(() => { resolving = null; });
  }
  return resolving;
}

// Специальный класс ошибки с типом, чтобы UI мог показать понятное сообщение.
// Тип читается через err.netErrorKey, оно совпадает с ключом локали в i18n (network.*)
export class ApiError extends Error {
  constructor(message, opts = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = opts.status || 0;            // HTTP статус (0 если сети нет)
    this.netErrorKey = opts.netErrorKey || null; // ключ локали типа 'network.timeout'
    this.serverError = opts.serverError || null; // оригинальный текст с сервера если есть
    this.blocked = opts.blocked || null;       // если 403 blocked: { kind: 'user'|'shop', reason }
    this.data = opts.data || null;             // полное тело ответа сервера (attemptsLeft, retryAfter и т.п.)
  }
}

async function request(method, path, body = null, opts = {}) {
  const token = localStorage.getItem('token');
  const headers = {};
  if (!opts.isFormData) headers['Content-Type'] = 'application/json';
  if (token && !opts.skipAuth) headers['Authorization'] = `Bearer ${token}`;

  // Выбор таймаута по контексту
  let timeoutMs = TIMEOUT_DEFAULT;
  if (opts.isFormData) timeoutMs = TIMEOUT_UPLOAD;
  else if (path.startsWith('/auth/')) timeoutMs = TIMEOUT_AUTH;
  if (opts.timeout) timeoutMs = opts.timeout;

  // Один fetch к конкретному хосту с текущим таймаутом.
  const doFetch = async (base) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const r = await fetch(`${base}${path}`, {
        method,
        headers,
        body: opts.isFormData ? body : (body ? JSON.stringify(body) : null),
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      return r;
    } catch (e) {
      clearTimeout(timeoutId);
      throw e;
    }
  };

  let res;
  const base = await getBase();
  try {
    res = await doFetch(base);
  } catch (e) {
    // Фиксированный адрес из окружения — без переключений.
    if (ENV_BASE) {
      if (e.name === 'AbortError') throw new ApiError('timeout', { netErrorKey: 'network.timeout' });
      throw new ApiError('network', { netErrorKey: 'network.offline' });
    }
    // Рабочий хост не ответил — возможно, сменилась сеть (дом → VPN, WiFi →
    // мобильный) или путь умер. Сбрасываем кэш и заново устраиваем гонку.
    resolvedBase = null;
    try { localStorage.removeItem(LS_BASE_KEY); } catch (e2) {}
    let newBase;
    try {
      newBase = await resolveBase();
    } catch (e2) {
      newBase = BASES.find(b => b !== base) || base;
    }
    try {
      res = await doFetch(newBase);
    } catch (e2) {
      if (e2.name === 'AbortError') {
        throw new ApiError('timeout', { netErrorKey: 'network.timeout' });
      }
      // Любая другая сетевая ошибка (нет интернета, DNS, отказ соединения, CORS)
      throw new ApiError('network', { netErrorKey: 'network.offline' });
    }
  }

  // Сервер ответил, но статус не ОК
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    // 403 blocked — особая ошибка, диспатчим глобальное событие чтобы
    // AuthProvider мог показать экран блокировки
    if (res.status === 403 && data && data.error === 'blocked') {
      const blockedInfo = { kind: data.kind || 'user', reason: data.reason || null };
      try {
        window.dispatchEvent(new CustomEvent('auth:blocked', { detail: blockedInfo }));
      } catch (e) {}
      throw new ApiError('blocked', {
        status: 403,
        blocked: blockedInfo,
        serverError: 'blocked',
      });
    }
    // 5xx — сервер сломан
    if (res.status >= 500) {
      throw new ApiError(data.error || 'server', {
        status: res.status,
        netErrorKey: 'network.server',
        serverError: data.error || null,
      });
    }
    // 4xx — у клиента нормальная ошибка (валидация, неверный пароль и т.д.)
    // Показываем текст с сервера как раньше — он уже на нужном языке (или хотя бы понятный)
    throw new ApiError(data.error || `HTTP ${res.status}`, {
      status: res.status,
      serverError: data.error || null,
      data,
    });
  }
  return rewriteMedia(data);
}

// ─── Ссылки на фото ──────────────────────────────────────────────
// Файлы лежат на одном сервере и доступны по ЛЮБОМУ из наших хостов,
// но в базе записан абсолютный адрес одного из них. Если сейчас рабочий
// другой хост (например, дома Cloudflare не открывается, а api4 да),
// картинки по записанному адресу не загрузятся.
// Поэтому в каждом ответе сервера подставляем адрес АКТИВНОГО хоста.
const MEDIA_ORIGINS = [
  'https://api.alsatuu.biz',
  'https://api4.alsatuu.biz',
  'https://api.alsatuu.codingkgz.store',
];

function activeOrigin() {
  const b = resolvedBase || ENV_BASE || BASES[0];
  return String(b).replace(/\/api\/?$/, '');
}

function swapOrigin(s) {
  if (s.length < 12 || s.indexOf('/uploads/') === -1) return s;
  for (const o of MEDIA_ORIGINS) {
    if (s.startsWith(o + '/uploads/')) {
      return activeOrigin() + s.slice(o.length);
    }
  }
  return s;
}

function rewriteMedia(v) {
  if (typeof v === 'string') return swapOrigin(v);
  if (Array.isArray(v)) {
    for (let i = 0; i < v.length; i++) v[i] = rewriteMedia(v[i]);
    return v;
  }
  if (v && typeof v === 'object') {
    for (const k in v) {
      if (Object.prototype.hasOwnProperty.call(v, k)) v[k] = rewriteMedia(v[k]);
    }
    return v;
  }
  return v;
}

// Приводит одиночную ссылку к активному хосту (для мест, где URL
// формируется вне ответов API).
export function mediaUrl(u) {
  return typeof u === 'string' ? swapOrigin(u) : u;
}

const get   = (p, opts) => request('GET', p, null, opts);
const post  = (p, b, opts) => request('POST', p, b, opts);
const patch = (p, b) => request('PATCH', p, b);
const del   = (p, b) => request('DELETE', p, b);

// ─── AUTH ─────────────────────────────────────────────────────
export const auth = {
  // Раздельная регистрация по типу учётки.
  // role — 'shop' | 'customer'.
  registerCustomer: (email, password, name) =>
    post('/auth/register-customer', { email, password, name }),
  registerShop: (email, password, name) =>
    post('/auth/register-shop', { email, password, name }),

  // Вход — role обязателен, чтобы выбрать правильную учётку по (email, role).
  login: (email, password, role) =>
    post('/auth/login', { email, password, role }),

  // Вход сотрудника: shopId + credential (login или PIN) + password (только для login-режима)
  employeeLogin: (shopId, credential, password) =>
    post('/auth/employee-login', { shopId, credential, password }),
  // ─── Сброс забытого пароля (публичные, без токена) ───
  // role — 'shop' | 'customer'; lang — язык письма
  forgotPassword: (email, role, lang) =>
    post('/auth/forgot-password', { email, role, lang }, { skipAuth: true }),
  verifyResetCode: (email, role, code) =>
    post('/auth/verify-reset-code', { email, role, code }, { skipAuth: true }),
  resetPassword: (email, role, code, newPassword) =>
    post('/auth/reset-password', { email, role, code, newPassword }, { skipAuth: true }),

  me:       () => get('/auth/me'),
  updateMe: (data) => patch('/auth/me', data),
  // Удалить свой аккаунт (с паролем)
  deleteAccount: (password) => post('/auth/delete-account', { password }),
};

// ─── МАГАЗИН ──────────────────────────────────────────────────
export const shop = {
  my:        () => get('/shop/my'),
  create:    (data) => post('/shop', data),
  update:    (data) => patch('/shop/my', data),
  // ─── PIN на удаление ───
  // { pinSet: bool }
  pinStatus:  () => get('/shop/my/delete-pin'),
  // body: { pin, password? } — password нужен, если PIN уже установлен
  pinSet:     (data) => request('PUT', '/shop/my/delete-pin', data),
  // body: { password } — снятие PIN подтверждается паролем аккаунта
  pinRemove:  (data) => del('/shop/my/delete-pin', data),
  // body: { pin } → { valid: bool }
  pinVerify:  (pin) => post('/shop/my/delete-pin/verify', { pin }),
  // Новые поля магазина (страна, корзина, доставка)
  updateExtras: (data) => patch('/shop/my/extras', data),
  // Модульность: { moduleConfig: { key: bool } } → обновлённый shop
  updateModules: (moduleConfig) => patch('/shop/my/modules', { moduleConfig }),
  // Дашборд аналитики: { dashboardConfig: { widget: bool } } → обновлённый shop
  updateDashboard: (dashboardConfig) => patch('/shop/my/dashboard', { dashboardConfig }),
  getById:   (id) => get(`/shop/${id}`),
  // Публичный каталог для гостя — без авторизации
  publicList: (params = {}) => {
    const qs = new URLSearchParams();
    if (params.country) qs.set('country', params.country);
    if (params.q)       qs.set('q', params.q);
    const tail = qs.toString() ? `?${qs}` : '';
    return get(`/shops/public${tail}`, { skipAuth: true });
  },
};

// ─── ПРОФИЛИ МОДУЛЕЙ ──────────────────────────────────────────
export const profiles = {
  // Активные профили для экрана регистрации
  list: () => get('/profiles'),
  // Суперадминский CRUD (используется в суперадминке)
  adminList:   () => get('/superadmin/profiles'),
  adminGet:    (id) => get(`/superadmin/profiles/${id}`),
  adminCreate: (data) => post('/superadmin/profiles', data),
  adminUpdate: (id, data) => patch(`/superadmin/profiles/${id}`, data),
  adminDelete: (id, force) => del(`/superadmin/profiles/${id}${force ? '?force=1' : ''}`),
};

// ─── КЭШ СПИСКОВ (товары, категории) ──────────────────────────
// Касса и каталог открываются сразу с прошлым списком, а свежий тянется в фоне
// (страница сама вызывает list() и подменяет данные). Кэш привязан к токену —
// другой вход его не увидит. Память + localStorage (переживает перезапуск APK);
// если localStorage переполнен — просто живём на кэше в памяти.
const DATA_CACHE_PREFIX = 'alsatuu:dc:';
const _dataMem = {};
const tokenTag = () => {
  const tk = localStorage.getItem('token');
  return tk ? tk.slice(-24) : null;
};

function readDataCache(name) {
  const tag = tokenTag();
  if (!tag) return null;
  const m = _dataMem[name];
  if (m && m.tag === tag) return m.data;
  try {
    const c = JSON.parse(localStorage.getItem(DATA_CACHE_PREFIX + name) || 'null');
    if (c && c.tag === tag && Array.isArray(c.data)) {
      c.data = rewriteMedia(c.data); // фото — на текущий активный хост
      _dataMem[name] = c;
      return c.data;
    }
  } catch (e) {}
  return null;
}

function writeDataCache(name, data) {
  const tag = tokenTag();
  if (!tag || !Array.isArray(data)) return;
  const rec = { tag, data };
  _dataMem[name] = rec;
  // Запись в localStorage — после отрисовки, чтобы не тормозить экран
  setTimeout(() => {
    if (_dataMem[name] !== rec) return; // уже пришли более свежие данные
    try { localStorage.setItem(DATA_CACHE_PREFIX + name, JSON.stringify(rec)); }
    catch (e) { try { localStorage.removeItem(DATA_CACHE_PREFIX + name); } catch (e2) {} }
  }, 0);
}

const cachedList = (name, path) => async () => {
  const data = await get(path);
  writeDataCache(name, data);
  return data;
};

// Очистка при выходе из аккаунта
export function clearDataCache() {
  for (const k of Object.keys(_dataMem)) delete _dataMem[k];
  try {
    Object.keys(localStorage)
      .filter(k => k.startsWith(DATA_CACHE_PREFIX))
      .forEach(k => localStorage.removeItem(k));
  } catch (e) {}
}

// ─── КАТЕГОРИИ ────────────────────────────────────────────────
export const categories = {
  list:       cachedList('categories', '/categories'),
  // Прошлый список из кэша (мгновенно) или null
  cached:     () => readDataCache('categories'),
  publicList: (shopId) => get(`/categories/public/${shopId}`, { skipAuth: true }),
  create:     (data) => post('/categories', data),
  update:     (id, data) => patch(`/categories/${id}`, data),
  remove:     (id) => del(`/categories/${id}`),
};

// ─── ТОВАРЫ ───────────────────────────────────────────────────
export const products = {
  list:    cachedList('products', '/products'),
  // Прошлый список из кэша (мгновенно) или null
  cached:  () => readDataCache('products'),
  catalog: (shopId) => get(`/products/catalog/${shopId}`, { skipAuth: true }),
  create:  (data) => post('/products', data),
  update:  (id, data) => patch(`/products/${id}`, data),
  remove:  (id) => del(`/products/${id}`),
  // Поиск по баркоду (EAN или GTIN из Data Matrix)
  // → { product, variant } или 404
  byBarcode: (code) => get(`/products/by-barcode/${encodeURIComponent(code)}`),
  expiring: () => get('/products/expiring'),
  // Внешний поиск по баркоду в Open Food Facts
  // → { found: bool, name?, brand?, image? }
  lookupExternal: (code) => get(`/products/lookup-external/${encodeURIComponent(code)}`),
  // Варианты товара (matrix)
  variants: {
    list:   (productId) => get(`/products/${productId}/variants`),
    create: (productId, data) => post(`/products/${productId}/variants`, data),
    update: (productId, variantId, data) => patch(`/products/${productId}/variants/${variantId}`, data),
    remove: (productId, variantId) => del(`/products/${productId}/variants/${variantId}`),
  },
};

// ─── СКЛАДСКИЕ ОПЕРАЦИИ (закупка / возврат / списание) ────────
export const stockOps = {
  list:   (params = {}) => {
    const qs = new URLSearchParams();
    if (params.days) qs.set('days', params.days);
    if (params.type) qs.set('type', params.type);
    const tail = qs.toString() ? `?${qs}` : '';
    return get(`/stock-ops${tail}`);
  },
  create: (data) => post('/stock-ops', data),
  remove: (id) => del(`/stock-ops/${id}`),
};

// ─── ОПЛАТА БАЛЛАМИ (через QR ожидания) ───────────────────────
export const payments = {
  // Кассир: создать ожидание оплаты.
  // payload: { items, discountType?, discountValue? } — для совместимости
  // принимает и старый формат (просто массив items)
  initPending: (payload) => {
    const body = Array.isArray(payload) ? { items: payload } : payload;
    return post('/payments/pending', body);
  },
  // Опрос статуса (касса) или детали (клиент). Без авторизации.
  getPending:    (token) => get(`/payments/pending/${token}`, { skipAuth: true }),
  // Кассир отменяет
  cancelPending: (token) => del(`/payments/pending/${token}`),
  // Клиент подтверждает оплату баллами (требует авторизации)
  redeemPending: (token) => post(`/payments/pending/${token}/redeem`),
};

// ─── ОТЧЁТЫ ───────────────────────────────────────────────────
export const reports = {
  // Отчёт по складу за период.
  // Можно передать from/to (ISO даты) или days (последние N дней).
  inventory: ({ from, to, days } = {}) => {
    const qs = new URLSearchParams();
    if (from) qs.set('from', typeof from === 'string' ? from : from.toISOString());
    if (to)   qs.set('to',   typeof to   === 'string' ? to   : to.toISOString());
    if (days) qs.set('days', String(days));
    const tail = qs.toString() ? `?${qs}` : '';
    return get(`/reports/inventory${tail}`);
  },
  // Полный PDF-отчёт. blocks: массив имён блоков (summary, sales, ...)
  full: ({ from, to, blocks } = {}) => {
    const qs = new URLSearchParams();
    if (from) qs.set('from', from instanceof Date ? from.toISOString() : from);
    if (to)   qs.set('to',   to instanceof Date ? to.toISOString()   : to);
    if (Array.isArray(blocks) && blocks.length) qs.set('blocks', blocks.join(','));
    const qsStr = qs.toString();
    return get(`/reports/full${qsStr ? '?' + qsStr : ''}`, { timeout: 30000 });
  },
};

// ─── ОПЛАТА БАЛЛАМИ ──────────────────────────────────────────
export const paymentRequests = {
  // магазин — создать запрос, получить токен
  create: (items) => post('/payment-requests', { items }),
  // клиент — получить детали по токену (после скана QR)
  get:    (token) => get(`/payment-requests/${token}`),
  // клиент — подтвердить оплату
  pay:    (token) => post(`/payment-requests/${token}/pay`),
  // магазин — проверить статус (polling)
  status: (token) => get(`/payment-requests/${token}/status`),
  // магазин — отменить
  cancel: (token) => post(`/payment-requests/${token}/cancel`),
};

// ─── ПРОДАЖИ ──────────────────────────────────────────────────
export const sales = {
  // payload: { items, discountType?, discountValue? } или массив items для совместимости
  create: (payload) => {
    const body = Array.isArray(payload) ? { items: payload } : payload;
    return post('/sales', body);
  },
  list:   (days = 30) => get(`/sales?days=${days}`),
  // Журнал продаж (вкладка «Отчёт») с пагинацией
  journal: (params = {}) => {
    const qs = Object.entries(params)
      .filter(([, v]) => v !== undefined && v !== null && v !== '')
      .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
      .join('&');
    return get(`/sales/journal${qs ? `?${qs}` : ''}`);
  },
};

// Розничные должники (модуль retailDebt)
export const debtors = {
  list:    (q) => get(`/debtors${q ? `?q=${encodeURIComponent(q)}` : ''}`),
  create:  (data) => post('/debtors', data),
  get:     (id) => get(`/debtors/${id}`),
  update:  (id, data) => patch(`/debtors/${id}`, data),
  // create:true — завести нового должника после «Добавить?» на кассе
  resolveScan: (code, create = false) => post('/debtors/resolve-scan', { code, create }),

  payment: (id, data) => post(`/debtors/${id}/payment`, data),
  remove:  (id) => del(`/debtors/${id}`),
};

// ─── ИНВЕНТАРИЗАЦИЯ ───────────────────────────────────────────
export const inventory = {
  start:    () => post('/inventory/sessions'),
  open:     () => get('/inventory/sessions/open'),
  addItem:  (sessionId, code, actual) => post(`/inventory/sessions/${sessionId}/items`, { code, actual }),
  removeItem: (itemId) => del(`/inventory/items/${itemId}`),
  close:    (sessionId) => post(`/inventory/sessions/${sessionId}/close`),
  cancel:   (sessionId) => del(`/inventory/sessions/${sessionId}`),
  history:  () => get('/inventory/sessions'),
  detail:   (id) => get(`/inventory/sessions/${id}`),
};

// ─── ЛОЯЛЬНОСТЬ ───────────────────────────────────────────────
export const loyalty = {
  redeem:  (shopId, token) => post('/loyalty/redeem', { shopId, token }),
  cards:   () => get('/loyalty/cards'),
  history: (shopId) => get(`/loyalty/history/${shopId}`),
  spend:   (shopId, pointsToSpend) => post('/loyalty/spend', { shopId, pointsToSpend }),
  receipts: (shopId) => get(`/loyalty/receipts${shopId ? `?shopId=${encodeURIComponent(shopId)}` : ''}`),
  receipt:  (id) => get(`/loyalty/receipts/${encodeURIComponent(id)}`),
};

// ─── СОТРУДНИКИ (только для владельцев) ───────────────────────
export const employees = {
  list:   () => get('/employees'),
  // data: { email, password, name, permissions: { cashier, products, ... } }
  create: (data) => post('/employees', data),
  // patch: { permissions?, password?, name? }
  update: (id, data) => patch(`/employees/${id}`, data),
  remove: (id) => del(`/employees/${id}`),
};

// ─── КОНТРАГЕНТЫ (поставщики и оптовые клиенты) ───────────────
// Доступно владельцу и admin-сотруднику.
export const counterparties = {
  // type: 'supplier' | 'wholesale' | 'all'
  list: (params = {}) => {
    const qs = new URLSearchParams();
    if (params.type)            qs.set('type', params.type);
    if (params.search)          qs.set('search', params.search);
    if (params.includeArchived) qs.set('includeArchived', '1');
    const tail = qs.toString() ? `?${qs}` : '';
    return get(`/counterparties${tail}`);
  },
  // Подтянуть публичные данные другого магазина Алсатуу
  lookupShop: (shopId) => get(`/counterparties/lookup-shop/${shopId}`),

  // ─── Рукопожатие связи между магазинами Alsatuu ───
  // Входящие запросы: «магазин X добавил вас как поставщика»
  linkRequests: () => get('/counterparties/link-requests'),
  // Принять — включает синхронизацию документов
  linkAccept:  (id) => post(`/counterparties/${id}/link-accept`, {}),
  // Отклонить — контрагент остаётся «для себя», синхронизации нет
  linkDecline: (id) => post(`/counterparties/${id}/link-decline`, {}),

  // ─── Синхронизация платежей (деньги требуют подтверждения) ───
  // Входящие: вторая сторона заявила платёж, жду моего подтверждения
  paymentsPending: () => get('/counterparties/payments/pending'),
  // Подтвердить получение → платёж учитывается у обоих
  paymentConfirm: (id) => post(`/counterparties/payments/${id}/confirm`, {}),
  // Отклонить (денег не было / сумма не та)
  paymentDecline: (id) => post(`/counterparties/payments/${id}/decline`, {}),

  // ─── Привязка розничного должника к аккаунту клиента ───
  // По личному QR клиента: сканом или картинкой из галереи (прислал в WhatsApp)
  linkCustomer:   (id, code) => post(`/counterparties/${id}/link-customer`, { code }),
  unlinkCustomer: (id) => post(`/counterparties/${id}/unlink-customer`, {}),
  // data: { name, phone, whatsapp, email, address, notes, isSupplier, isWholesaleCustomer, linkedShopId? }
  create: (data) => post('/counterparties', data),
  update: (id, data) => patch(`/counterparties/${id}`, data),
  remove: (id) => del(`/counterparties/${id}`),

  // Карточка с агрегатами и долгом
  summary:     (id) => get(`/counterparties/${id}/summary`),
  // Сводка по всем контрагентам с ненулевыми долгами
  debts:       () => get('/counterparties/debts'),
  // История операций (закупки)
  operations:  (id) => get(`/counterparties/${id}/operations`),
  // История платежей
  payments:    (id) => get(`/counterparties/${id}/payments`),
  // Записать платёж: { direction: 'outgoing'|'incoming', amount, method?, note? }
  addPayment:    (id, data) => post(`/counterparties/${id}/payments`, data),
  // Удалить платёж по его id
  removePayment: (paymentId) => del(`/counterparties/payments/${paymentId}`),
};

// ─── ПРИХОДНЫЕ НАКЛАДНЫЕ (поставки от поставщиков) ────────────
// Зеркало оптовых отгрузок: там мы отдаём товар, тут — принимаем.
export const purchases = {
  // Список приходов, опционально по поставщику
  list:   (counterpartyId) => get(`/purchases${counterpartyId ? `?counterpartyId=${counterpartyId}` : ''}`),
  // Одна накладная
  get:    (id) => get(`/purchases/${id}`),
  // Создать: { counterpartyId, note?, items: [{ productId, variantId?, qty, costPrice }] }
  create: (data) => post('/purchases', data),
  // Отменить приход (откатывает остатки)
  remove: (id) => del(`/purchases/${id}`),

  // ─── Фаза 2: накладные, прилетевшие от поставщика ───
  // Сколько ждут приёмки (для бейджа)
  pendingCount: () => get('/purchases/pending/count'),
  // Принять ПО ФАКТУ: { items: [{ id?, productId, variantId?, qty, costPrice }], note? }
  // id — строка из накладной поставщика; без id — новая строка (пересорт/довоз)
  accept: (id, data) => post(`/purchases/${id}/accept`, data),
};

// ─── ОПТОВЫЕ ОТГРУЗКИ (накладные) ─────────────────────────────
// ─── КЛИЕНТ: личный код и мои долги ───────────────────────────
export const customerDebts = {
  // Данные для личного QR (магазин сканирует его, чтобы привязать должника)
  myCode: () => get('/customer/my-code'),
  // Мои долги по всем магазинам
  list:   () => get('/customer/debts'),
  // Детали: что брал (состав покупок) и история платежей
  get:    (cpId) => get(`/customer/debts/${cpId}`),
};

// ─── О ПРИЛОЖЕНИИ (публичное, без токена) ────────────────────
export const app = {
  // Контакты поддержки. С сервера, а не из кода: сменил номер в .env —
  // обновилось у всех, включая старые APK (механизма обновлений пока нет).
  support: () => get('/app/support', { skipAuth: true }),
  // Объявления-попап из суперадминки (активные, для этой аудитории и языка)
  announcements: (audience, lang) => {
    const qs = new URLSearchParams();
    if (audience) qs.set('audience', audience);
    if (lang) qs.set('lang', String(lang).slice(0, 2));
    return get(`/app/announcements?${qs}`, { skipAuth: true });
  },
};

export const wholesaleSales = {
  // Список накладных, опционально с фильтром по контрагенту
  list:   (counterpartyId) => get(`/wholesale-sales${counterpartyId ? `?counterpartyId=${counterpartyId}` : ''}`),
  // Одна накладная
  get:    (id) => get(`/wholesale-sales/${id}`),
  // Создать: { counterpartyId, items: [...], codes?: [{ code, seq, productId?, name?, gtin? }], note? }
  create: (data) => post('/wholesale-sales', data),
  // Проверить код DataMatrix по истории отгрузок → { used, saleNumber?, date?, name? }
  checkCode: (code) => get(`/wholesale-sales/check-code?code=${encodeURIComponent(code)}`),
  // Отменить (восстанавливает остатки)
  remove: (id) => del(`/wholesale-sales/${id}`),

  // ─── Фаза 2: как покупатель принял отгрузку ───
  // Построчно: заявлено / принято / расхождение
  acceptance: (id) => get(`/wholesale-sales/${id}/acceptance`),
  // Решение по недостаче: 'return' (вернуть на склад) | 'writeoff' (списать как бой)
  resolve: (id, mode) => post(`/wholesale-sales/${id}/resolve`, { mode }),
};

// ─── БРОНЬ DM-КОДОВ (параллельная сборка опта) ────────────────
export const dmReservations = {
  // Занять код: { ok:true } | { ok:false, busy:true, by }
  reserve:   (code) => post('/dm-reservations/reserve', { code }),
  // Продлить мои брони (клиент шлёт раз в ~5 мин)
  heartbeat: (codes) => post('/dm-reservations/heartbeat', { codes }),
  // Снять брони (убрал код / закрыл сборку)
  release:   (codes) => post('/dm-reservations/release', Array.isArray(codes) ? { codes } : { code: codes }),
};

// ─── КАФЕ: ЗАКАЗЫ (модуль «Официант»/«Кухня») ─────────────────
export const cafeOrders = {
  // Список заказов: status = open | closed | cancelled | all
  list:   (status = 'open') => get(`/cafe-orders?status=${status}`),
  // Один заказ
  get:    (id) => get(`/cafe-orders/${id}`),
  // Создать (принять): { tableLabel, items: [{ productId, variantId?, name, qty, unit, salePrice, costPrice? }], note? }
  create: (data) => post('/cafe-orders', data),
  // Редактировать открытый: { tableLabel?, note?, addItems?, removeItemIds? }
  update: (id, data) => patch(`/cafe-orders/${id}`, data),
  // Кухня: статус блюда — 'pending' | 'cooking' | 'done'
  cook:   (id, itemId, status) => post(`/cafe-orders/${id}/items/${itemId}/cook`, { status }),
  // Кухня: решение по запросу удаления — approve: bool
  resolveRemoval: (id, itemId, approve) => post(`/cafe-orders/${id}/items/${itemId}/resolve-removal`, { approve }),
  // Закрыть заказ = продажа: { discountType?, discountValue?, counterpartyId?, paidNow? }
  close:  (id, data = {}) => post(`/cafe-orders/${id}/close`, data),
  // Отменить заказ (склад не трогается)
  cancel: (id) => post(`/cafe-orders/${id}/cancel`, {}),
};

// ─── ЖУРНАЛ ДЕЙСТВИЙ (только владелец) ────────────────────────
export const audit = {
  // Лента с фильтрами и cursor-пагинацией.
  // params: { from?, to?, actorId?, action?, actionPrefix?, cursor?, limit? }
  list: (params = {}) => {
    const qs = Object.entries(params)
      .filter(([, v]) => v !== undefined && v !== null && v !== '')
      .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
      .join('&');
    return get(`/audit${qs ? `?${qs}` : ''}`);
  },
  // Сотрудники магазина для выпадашки фильтра
  actors: () => get('/audit/actors'),
};

// ─── ЗАГРУЗКА ФОТО (multipart/form-data) ──────────────────────
// Перед отправкой каждое изображение прогоняется через canvas:
// конвертируется в JPEG 85% и ресайзится до макс. 1280px по большой стороне.
// Это нужно потому что Capacitor WebView криво кодирует большие PNG (особенно скриншоты)
// в multipart-запросы, файл может прийти на бэк битым (Sharp ругается "premature end").
// Конвертация через canvas всегда выдаёт корректный JPEG.
function resizeImageToJpeg(file, maxSize = 1280, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Не удалось прочитать файл'));
    reader.onload = (e) => {
      const img = new Image();
      img.onerror = () => reject(new Error('Не удалось декодировать изображение'));
      img.onload = () => {
        let { width, height } = img;
        if (width > maxSize || height > maxSize) {
          if (width > height) {
            height = Math.round(height * (maxSize / width));
            width  = maxSize;
          } else {
            width  = Math.round(width * (maxSize / height));
            height = maxSize;
          }
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        // Заливаем белым фоном — на случай PNG с прозрачностью (она в JPEG не сохранится)
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (blob) => {
            if (!blob) return reject(new Error('Не удалось сжать изображение'));
            // Делаем из blob — File, чтобы у поля было имя для multer
            const out = new File([blob], (file.name || 'photo').replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' });
            resolve(out);
          },
          'image/jpeg',
          quality
        );
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}

export const upload = {
  photos: async (files) => {
    if (!files || !files.length) return { urls: [] };
    const formData = new FormData();
    for (const f of files) {
      try {
        const compressed = await resizeImageToJpeg(f);
        formData.append('photos', compressed);
      } catch (e) {
        // Если по какой-то причине не вышло сжать — отправляем как есть
        formData.append('photos', f);
      }
    }
    return post('/upload/products', formData, { isFormData: true });
  },
  remove: (url) => del('/upload/products', { url }),
};

// ─── AI ГЕНЕРАЦИЯ (для магазинов) ────────────────────────────────
// Бэк ходит в Gemini Nano Banana. Запрос multipart/form-data.
async function aiGenerateRequest(formData) {
  const token = localStorage.getItem('token');
  const url = `${await getBase()}/ai/generate`;
  let res;
  try {
    const controller = new AbortController();
    const tid = setTimeout(() => controller.abort(), 540_000);  // 9 минут (бэк может делать 2 попытки по 4 мин)
    res = await fetch(url, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData,
      signal: controller.signal,
    });
    clearTimeout(tid);
  } catch (e) {
    if (e.name === 'AbortError') {
      throw new ApiError('timeout', { netErrorKey: 'network.timeout' });
    }
    throw new ApiError('network', { netErrorKey: 'network.offline' });
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new ApiError(data.message || data.error || `HTTP ${res.status}`, {
      status: res.status,
      serverError: data.error || null,
    });
  }
  return data;
}

export const ai = {
  // Список моделей для виртуальной примерки (с фильтрами)
  models: (params = {}) => {
    const qs = new URLSearchParams();
    if (params.gender)   qs.set('gender', params.gender);
    if (params.ageGroup) qs.set('ageGroup', params.ageGroup);
    if (params.tag)      qs.set('tag', params.tag);
    const tail = qs.toString() ? `?${qs}` : '';
    return get(`/ai/models${tail}`);
  },

  // Список подставок для типа on_pedestal
  pedestals: (params = {}) => {
    const qs = new URLSearchParams();
    if (params.style) qs.set('style', params.style);
    if (params.tag)   qs.set('tag', params.tag);
    const tail = qs.toString() ? `?${qs}` : '';
    return get(`/ai/pedestals${tail}`);
  },

  // Статус лимита подписки магазина
  status: () => get('/ai/status'),

  // Универсальная генерация. params:
  //   type: 'try_on' | 'studio_photo' | 'on_pedestal'
  //   product: File (или Blob)
  //   productDescription: string (обязательно)
  //   modelId?: string (для try_on)
  //   pedestalId?: string (для on_pedestal — теперь обязательно)
  //   aspectRatio?: '1:1'|'4:5'|'3:4'|'16:9'|'9:16'
  generate: async (params) => {
    const fd = new FormData();
    fd.append('type', params.type);
    fd.append('productDescription', params.productDescription || '');
    if (params.product) fd.append('product', params.product);
    if (params.modelId)        fd.append('modelId', params.modelId);
    if (params.pedestalId)     fd.append('pedestalId', params.pedestalId);
    if (params.aspectRatio)    fd.append('aspectRatio', params.aspectRatio);
    return aiGenerateRequest(fd);
  },

  // История генераций магазина
  history: (limit = 30) => get(`/ai/generations?limit=${limit}`),

  // Отметить что результат применён к товару
  apply: (generationId, productId) =>
    post(`/ai/generations/${generationId}/apply`, { productId: productId || null }),
};

// ИИ-ассистент аналитики (чат по реальным цифрам магазина)
export const aiAssistant = {
  // Состояние текстового лимита (отдельно от генерации картинок)
  status: () => get('/ai-assistant/status'),

  // Задать вопрос. { message, chatId?, days? } → { chatId, reply, used, limit }
  chat: ({ message, chatId = null, days } = {}) =>
    post('/ai-assistant/chat', { message, chatId, ...(days ? { days } : {}) }),

  // Список чатов магазина
  chats: (limit = 30) => get(`/ai-assistant/chats?limit=${limit}`),

  // Сообщения конкретного чата
  chatMessages: (id) => get(`/ai-assistant/chats/${id}`),

  // Память агента (политики/факты)
  memory:       () => get('/ai-assistant/memory'),
  addMemory:    (kind, content) => post('/ai-assistant/memory', { kind, content }),
  removeMemory: (id) => del(`/ai-assistant/memory/${id}`),
};

// ─── ИЗБРАННЫЕ МАГАЗИНЫ (localStorage, без бэка) ──────────────
const FAV_KEY = 'alsatuu:favorites';
const FAV_CACHE_KEY = 'alsatuu:favorites:cache';

export const favorites = {
  ids: () => {
    try { return JSON.parse(localStorage.getItem(FAV_KEY) || '[]'); }
    catch { return []; }
  },
  has: (shopId) => favorites.ids().includes(shopId),
  add: async (shopId) => {
    const cleanId = String(shopId || '').trim();
    if (!cleanId) throw new Error('Введите ID магазина');
    const ids = favorites.ids();
    if (ids.includes(cleanId)) return;
    const data = await shop.getById(cleanId);
    ids.push(cleanId);
    localStorage.setItem(FAV_KEY, JSON.stringify(ids));
    const cache = JSON.parse(localStorage.getItem(FAV_CACHE_KEY) || '{}');
    cache[cleanId] = data;
    localStorage.setItem(FAV_CACHE_KEY, JSON.stringify(cache));
    return data;
  },
  remove: (shopId) => {
    const ids = favorites.ids().filter(id => id !== shopId);
    localStorage.setItem(FAV_KEY, JSON.stringify(ids));
    const cache = JSON.parse(localStorage.getItem(FAV_CACHE_KEY) || '{}');
    delete cache[shopId];
    localStorage.setItem(FAV_CACHE_KEY, JSON.stringify(cache));
  },
  list: async () => {
    const ids = favorites.ids();
    if (!ids.length) return [];
    const cache = JSON.parse(localStorage.getItem(FAV_CACHE_KEY) || '{}');
    const results = await Promise.all(ids.map(async (id) => {
      try {
        const data = await shop.getById(id);
        cache[id] = data;
        return data;
      } catch (e) {
        return cache[id] || { id, name: id, _stale: true };
      }
    }));
    localStorage.setItem(FAV_CACHE_KEY, JSON.stringify(cache));
    return results;
  },
};

// ─── КОРЗИНА (localStorage, отдельно для каждого магазина) ────
const cartKey = (shopId) => `alsatuu:cart:${shopId}`;

export const cart = {
  get: (shopId) => {
    try { return JSON.parse(localStorage.getItem(cartKey(shopId)) || '[]'); }
    catch { return []; }
  },
  count: (shopId) => cart.get(shopId).reduce((s, i) => s + i.qty, 0),
  // product: { id, name, salePrice, unit, photo, ... } или вариант с variantId
  // Если передан вариант (variantId есть) — строка отдельная для каждого варианта.
  add: (shopId, product, qty = 1) => {
    const items = cart.get(shopId);
    const variantId = product.variantId || null;
    const cartKeyStr = `${product.id}::${variantId || 'default'}`;
    const existing = items.find(i => (i.cartKey || `${i.id}::${i.variantId || 'default'}`) === cartKeyStr);
    if (existing) existing.qty += qty;
    else items.push({
      id: product.id,
      variantId,
      cartKey: cartKeyStr,
      name: product.name,
      salePrice: product.salePrice,
      unit: product.unit,
      photo: product.photo,
      qty,
    });
    localStorage.setItem(cartKey(shopId), JSON.stringify(items));
    return items;
  },
  setQty: (shopId, cartKeyStr, qty) => {
    let items = cart.get(shopId);
    // Принимаем cartKey или старый productId — мапим на cartKey
    const matchKey = (i) => (i.cartKey || `${i.id}::${i.variantId || 'default'}`) === cartKeyStr
                          || i.id === cartKeyStr;
    if (qty <= 0) items = items.filter(i => !matchKey(i));
    else items = items.map(i => matchKey(i) ? { ...i, qty } : i);
    localStorage.setItem(cartKey(shopId), JSON.stringify(items));
    return items;
  },
  remove: (shopId, cartKeyStr) => {
    const matchKey = (i) => (i.cartKey || `${i.id}::${i.variantId || 'default'}`) === cartKeyStr
                          || i.id === cartKeyStr;
    const items = cart.get(shopId).filter(i => !matchKey(i));
    localStorage.setItem(cartKey(shopId), JSON.stringify(items));
    return items;
  },
  clear: (shopId) => localStorage.removeItem(cartKey(shopId)),
  total: (shopId) => cart.get(shopId).reduce((s, i) => s + i.qty * i.salePrice, 0),
};

